/**
 * /tenants/$slug/security/logon-certificates — the Windows sign-in console.
 *
 * Thin shim: prefetch readiness, mount the feature page.
 *
 * The loader swallows a rejection deliberately. An operator whose readiness read fails — a 403 from
 * a role that lost the permission, a 500 from the database — belongs on the page with its banner and
 * retry button, not on the router's error screen with no way back.
 *
 * Only readiness is prefetched. It is the question the page exists to answer and the one the
 * operator is waiting on; the revocation list is almost always empty and can arrive a moment later
 * without anybody noticing.
 *
 * The certificate lifetime is prefetched too, and for a narrower reason than "it is above the fold":
 * its card is the one that carries a warning, and a warning that arrives after the page has settled
 * is a warning an operator has already scrolled past.
 */

import { createFileRoute } from "@tanstack/react-router";
import {
  fetchCertificateProfile,
  fetchReadiness,
} from "@/features/logon-certificates/queries";
import { LogonCertificatesPage } from "@/features/logon-certificates/pages/logon-certificates-page";
import { queryKeys } from "@/lib/query-keys";

export const Route = createFileRoute("/_authed/tenants/$slug/security/logon-certificates/")({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient
        .ensureQueryData({
          queryKey: queryKeys.logonCertificates.readiness(context.tenant.id),
          queryFn: () => fetchReadiness(context.tenant.id),
        })
        .catch(() => undefined),
      context.queryClient
        .ensureQueryData({
          queryKey: queryKeys.logonCertificates.profile(context.tenant.id),
          queryFn: () => fetchCertificateProfile(context.tenant.id),
        })
        .catch(() => undefined),
    ]),
  component: LogonCertificatesPage,
});
